import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Store, Loader2, ArrowLeft, TrendingUp, Users, Zap } from "lucide-react";
import LegalConsentCheckbox from "@/components/LegalConsentCheckbox";

export default function VendorLogin() {
  const navigate = useNavigate();
  const { vendorLoginToken } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", store_name: "", city: "", code: "", new_password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [accepted, setAccepted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const go = (m) => { setMode(m); setError(""); setInfo(""); setAccepted(false); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    if (mode === "forgot") {
      if (!form.email) return setError("Inserisci la tua email.");
      setLoading(true);
      try {
        await api.post("/auth/forgot-password", { email: form.email.trim().toLowerCase() });
        setInfo("Se l'email è registrata, riceverai un codice di recupero. Controlla anche lo spam.");
        setMode("reset");
      } catch (err) { setError(errMessage(err)); } finally { setLoading(false); }
      return;
    }
    if (mode === "reset") {
      if (!form.code || !form.new_password) return setError("Inserisci codice e nuova password.");
      setLoading(true);
      try {
        const r = await api.post("/auth/reset-password", { email: form.email.trim().toLowerCase(), code: form.code.trim(), new_password: form.new_password.trim() });
        vendorLoginToken(r.data);
        navigate(r.data.vendor.store_ready ? "/vendor" : "/vendor/store");
      } catch (err) { setError(errMessage(err)); } finally { setLoading(false); }
      return;
    }
    if (!form.email || !form.password) return setError("Inserisci email e password.");
    if (mode === "register" && !form.store_name) return setError("Inserisci il nome del negozio.");
    if (mode === "register" && !accepted) return setError("Per registrarti devi accettare i Termini e Condizioni e la Privacy Policy.");
    setLoading(true);
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register"
        ? { email: form.email.trim().toLowerCase(), password: form.password.trim(), store_name: form.store_name, city: form.city.trim(), category: "altro", accepted_terms: true }
        : { email: form.email.trim().toLowerCase(), password: form.password.trim() };
      const r = await api.post(path, payload);
      vendorLoginToken(r.data);
      navigate(r.data.vendor.store_ready ? "/vendor" : "/vendor/store");
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    if (mode === "register" && !accepted) { setError("Per registrarti devi accettare i Termini e Condizioni e la Privacy Policy."); return; }
    localStorage.setItem("kn_oauth_role", "vendor");
    localStorage.setItem("kn_oauth_return", "/vendor");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between kn-gradient text-white p-12 relative overflow-hidden">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-white/90 font-semibold relative z-10">
          <ArrowLeft className="h-4 w-4" /> Torna alle offerte
        </button>
        <div className="relative z-10">
          <div className="h-14 w-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
            <Store className="h-7 w-7" />
          </div>
          <h1 className="text-4xl font-extrabold leading-tight">Fai crescere il tuo negozio con KoupoNow</h1>
          <p className="text-white/85 mt-3 text-lg">Pubblica un'offerta in 30 secondi e raggiungi i clienti vicino a te.</p>
          <div className="mt-8 space-y-4">
            <Feat icon={Zap} title="Pubblica in un attimo" sub="Offerte del giorno, sconti, 2x1 e prezzi fissi." />
            <Feat icon={Users} title="Fidelizza i clienti" sub="Follower, messaggi automatici e coupon fedeltà." />
            <Feat icon={TrendingUp} title="Traccia gli incassi" sub="Valida in cassa e vedi i risultati in tempo reale." />
          </div>
        </div>
        <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-white/10" />
        <div className="absolute top-20 -right-10 h-40 w-40 rounded-full bg-white/10" />
        <div className="relative z-10 text-white/60 text-sm">© KoupoNow</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          <button onClick={() => navigate("/")} className="lg:hidden flex items-center gap-2 text-slate-500 font-semibold mb-6">
            <ArrowLeft className="h-4 w-4" /> Offerte
          </button>
          <div className="lg:hidden h-12 w-12 rounded-2xl kn-gradient flex items-center justify-center mb-4">
            <Store className="h-6 w-6 text-white" />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900">
            {mode === "login" ? "Accedi al tuo negozio" : mode === "register" ? "Registra il tuo negozio" : mode === "forgot" ? "Recupera la password" : "Nuova password"}
          </h2>
          <p className="text-slate-500 mt-1">Area commercianti KoupoNow</p>

          <form onSubmit={submit} className="mt-6 space-y-3.5">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="v-store">Nome negozio</Label>
                <Input id="v-store" data-testid="vendor-store-name" value={form.store_name} onChange={set("store_name")} placeholder="Es. Bar Centrale" className="h-12 rounded-xl" />
              </div>
            )}
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="v-city">Città / Zona di interesse <span className="text-slate-400 font-normal">(facoltativo)</span></Label>
                <Input id="v-city" data-testid="vendor-city" value={form.city} onChange={set("city")} placeholder="Es. Pesaro" className="h-12 rounded-xl" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="v-email">Email</Label>
              <Input id="v-email" data-testid="vendor-email" type="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={form.email} onChange={set("email")} placeholder="negozio@esempio.it" className="h-12 rounded-xl" disabled={mode === "reset"} />
            </div>
            {(mode === "login" || mode === "register") && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="v-pass">Password</Label>
                  {mode === "login" && (
                    <button type="button" data-testid="vendor-forgot-link" onClick={() => go("forgot")} className="text-xs text-[#E63946] font-semibold">Password dimenticata?</button>
                  )}
                </div>
                <PasswordInput id="v-pass" testId="vendor-password" value={form.password} onChange={set("password")} placeholder="••••••••" className="h-12 rounded-xl" />
              </div>
            )}
            {mode === "reset" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="v-code">Codice ricevuto via email</Label>
                  <Input id="v-code" data-testid="vendor-reset-code" inputMode="numeric" value={form.code} onChange={set("code")} placeholder="123456" className="h-12 rounded-xl tracking-widest" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="v-newpass">Nuova password</Label>
                  <PasswordInput id="v-newpass" testId="vendor-new-password" autoComplete="new-password" value={form.new_password} onChange={set("new_password")} placeholder="••••••••" className="h-12 rounded-xl" />
                </div>
              </>
            )}
            {info && <p className="text-sm text-emerald-600 font-medium" data-testid="vendor-auth-info">{info}</p>}
            {error && <p className="text-sm text-[#DC2626] font-medium" data-testid="vendor-auth-error">{error}</p>}
            {mode === "register" && (
              <div className="pt-0.5">
                <LegalConsentCheckbox checked={accepted} onChange={setAccepted} testId="vendor-legal-consent" />
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="vendor-submit" className="w-full h-12 rounded-xl font-bold text-base">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "login" ? "Accedi" : mode === "register" ? "Registrati" : mode === "forgot" ? "Invia codice" : "Reimposta password"}
            </Button>

            {(mode === "forgot" || mode === "reset") && (
              <button type="button" data-testid="vendor-back-login" onClick={() => go("login")} className="flex items-center justify-center gap-1.5 w-full text-sm text-slate-500 font-semibold">
                <ArrowLeft className="h-4 w-4" /> Torna al login
              </button>
            )}

            {(mode === "login" || mode === "register") && (
              <>
                <div className="flex items-center gap-3 py-1">
                  <div className="h-px flex-1 bg-slate-200" /><span className="text-xs text-slate-400">oppure</span><div className="h-px flex-1 bg-slate-200" />
                </div>
                <Button type="button" variant="outline" onClick={googleLogin} data-testid="vendor-google" className="w-full h-12 rounded-xl font-semibold gap-2">
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" /> Continua con Google
                </Button>
              </>
            )}
          </form>

          {(mode === "login" || mode === "register") && (
            <p className="text-center text-sm text-slate-500 mt-5">
              {mode === "login" ? "Non hai un negozio su KoupoNow?" : "Hai già un account?"}{" "}
              <button data-testid="vendor-toggle" onClick={() => go(mode === "login" ? "register" : "login")} className="text-[#E63946] font-bold">
                {mode === "login" ? "Registrati" : "Accedi"}
              </button>
            </p>
          )}

          <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-400" data-testid="vendor-legal-footer">
            <a href="/legal/termini" target="_blank" rel="noopener noreferrer" className="hover:text-[#E63946] font-semibold">Termini</a>
            <span>·</span>
            <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-[#E63946] font-semibold">Privacy</a>
            <span>·</span>
            <a href="/legal/cookie" target="_blank" rel="noopener noreferrer" className="hover:text-[#E63946] font-semibold">Cookie</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feat({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>
      <div><p className="font-bold">{title}</p><p className="text-white/80 text-sm">{sub}</p></div>
    </div>
  );
}
